import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/base/ArticleCard';
import { useSeo } from '@/hooks/useSeo';
import { articles } from '@/mocks/articles';

const SITE_URL = 'https://luosai123.cn';

export default function GuidePage() {
  const categoryNames = useMemo(() => Array.from(new Set(articles.map((item) => item.category))), []);
  const [activeCategory, setActiveCategory] = useState('全部');

  const filtered = useMemo(
    () => (activeCategory === '全部' ? articles : articles.filter((item) => item.category === activeCategory)),
    [activeCategory],
  );

  const [lead, ...rest] = articles;

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '发布指南',
        url: `${SITE_URL}/guide`,
        description: '从材料整理、渠道选择到隐私保护与事实核查的爆料发布指南合集。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: '发布指南文章列表',
        numberOfItems: articles.length,
        itemListElement: articles.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.title,
          url: `${SITE_URL}/guide/${item.slug}`,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: '发布指南', item: `${SITE_URL}/guide` },
        ],
      },
    ],
    [],
  );

  useSeo({
    title: '发布指南 - 爆料材料整理与投递技巧｜爆料网上',
    description:
      '爆料网上发布指南从时间线整理、材料自证、渠道匹配到隐私自保与事实核查，把新手最容易踩坑的环节拆解成可执行步骤，帮你提高线索被认真对待的概率。',
    keywords: '爆料指南,投稿技巧,材料整理,隐私保护,事实核查,爆料网上',
    path: '/guide',
    jsonLd,
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '发布指南' }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">发布指南</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            同样一条线索，投出去的结果可能完全不同。这里整理的是流程层面的经验：
            材料怎么备、渠道怎么挑、身份怎么护、信息怎么核。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-12">
        <section aria-labelledby="lead-guide" className="mb-12">
          <h2 id="lead-guide" className="sr-only">
            重点推荐指南
          </h2>
          <article className="grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:grid-cols-2">
            <Link to={`/guide/${lead.slug}`} className="block cursor-pointer">
              <div className="h-56 w-full overflow-hidden bg-background-100 lg:h-full lg:min-h-[320px]">
                <img
                  src={lead.cover}
                  alt={`${lead.title} 配图`}
                  title={`${lead.title} - 爆料网上发布指南`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </Link>
            <div className="flex flex-col justify-center p-6 md:p-10">
              <span className="inline-flex w-fit items-center gap-1.5 rounded-sm bg-primary-100 px-2.5 py-1 text-xs text-primary-700">
                <i className="ri-bookmark-line"></i>入门首选
              </span>
              <h3 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950">
                <Link to={`/guide/${lead.slug}`} className="transition-colors hover:text-primary-600 cursor-pointer">
                  {lead.title}
                </Link>
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{lead.excerpt}</p>
              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                <span>{lead.category}</span>
                <span>{lead.date}</span>
                <span>约 {lead.readTime}</span>
              </div>
              <Link
                to={`/guide/${lead.slug}`}
                className="mt-6 inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                开始阅读<i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </article>
        </section>

        <section>
          <div className="mb-6 flex flex-wrap items-center gap-2">
            {['全部', ...categoryNames].map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => setActiveCategory(name)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
                  activeCategory === name
                    ? 'bg-accent-500 text-primary-950'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {(activeCategory === '全部' ? rest : filtered).map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        </section>

        <div className="mt-14 rounded-lg border border-background-200 bg-secondary-50 p-6 md:p-10">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <h2 className="font-heading text-xl font-bold text-foreground-950">
                看完还是不确定该投哪里？
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                把你手上材料的大致情况说一下，我们帮你判断适合的类别与渠道方向，不收取任何费用。
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-md bg-secondary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-500 cursor-pointer"
            >
              <i className="ri-question-answer-line"></i>去问一句
            </Link>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}